import {hel_resizeWindow, hel_reflectCanvasSize} from './logics/callCGui';
import {
  INITIAL_MIXER_DISTANCE_SETTING,
  INITIAL_MIXER_FADER_SETTING,
  INITIAL_MIXER_MUTE_SETTING,
  INITIAL_MIXER_SETTING
} from './logics/const';
import {isIntelWebGLPlatform} from './logics/dataOps';
import {defineSetstateForHeliModule} from './logics/globals';
import {setMainCanvasDOMElement} from './logics/mainCanvasDOM';
import {hel_show_fatal_error_dialog} from './logics/widgetErrorDialog';

import type AgoraClient from './networklib/AgoraClient';
import type {
  ProcedureCallFunc,
  ProcedureOnclickedAvatarCallFunc,
  ProcedureOnloadedCallFunc,
  ProcedureOnresizeCallFunc,
  ProcedureSkywayCommandCallFunc,
  ProcedureSkywayMyinfoCallFunc
} from './types/internal';
import type Hls from 'hls.js';
import type {HlsConfig} from 'hls.js';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  // config
  var g_WorldID: string | undefined;
  var g_Skyway_RoomID: string | undefined;
  var g_Spot: string | undefined;
  var g_ShowWorldSelect: boolean | undefined;
  var g_Logined: boolean | undefined;
  var g_CanvasFileName: string | undefined;
  var g_PackFileList: string[] | undefined;
  var g_CompImageListFileName: string | undefined;
  var g_CompImageListUseCDN: boolean | undefined;
  /** For send UserName from javascript to c++. */
  var g_CurrentUserName: string | undefined;
  var g_MainCanvas: HTMLCanvasElement | undefined;
  var g_CanvasIsPortrait: boolean | undefined;

  // render
  /** @remarks C++からアクセスあり */
  var g_UseWebGL10: boolean | undefined;
  var g_extPVRTC: WEBGL_compressed_texture_pvrtc | null | undefined;
  var g_TextureCompression_PVRTC: boolean | undefined;
  var g_extETC2: WEBGL_compressed_texture_etc | null | undefined;
  var g_TextureCompression_ETC2: boolean | undefined;
  var g_extASTC: WEBGL_compressed_texture_astc | null | undefined;
  var g_TextureCompression_ASTC: boolean | undefined;
  var g_extDXT: unknown;
  var g_TextureCompression_DXT: boolean | undefined;
  var g_Use_EXT_shader_texture_lod: boolean | undefined;
  var g_Use_EXT_color_buffer_half_float: boolean | undefined;
  var g_EXT_color_buffer_float: EXT_color_buffer_float | null | undefined;
  var g_EXT_color_buffer_half_float: EXT_color_buffer_half_float | null | undefined;
  var g_Use_EXT_color_buffer: boolean | undefined;
  var g_OES_texture_half_float: OES_texture_half_float | null | undefined;
  var g_EXT_shader_texture_lod: EXT_shader_texture_lod | null | undefined;

  // audio
  var g_hel_MixerTabActive: number | undefined;
  var g_hel_MixerFade: [number, number, number, number, number] | undefined;
  var g_hel_MixerDistance: [number, number, number, number, number] | undefined;
  var g_hel_MixerMute:
    | Parameters<InstanceType<typeof AgoraClient>['importEngineMasterVolume']>[1]
    | undefined;
  var g_hel_MixerMasterVolume: number | undefined;
  var g_hel_MixerMasterMute: number | undefined;
  var g_hel_UseLegacyAudio: boolean | undefined;

  /**
   * @remarks
   * C++からアクセスあり
   */
  var g_hel_Mixer: [number, number, number, number, number] | undefined;

  // video
  var g_Video: HTMLVideoElement | null | undefined;
  var g_videocanplay: boolean | undefined;
  var g_videopaused: boolean | undefined;
  var g_videoended: boolean | undefined;
  var g_VideoAudioContext: AudioContext | null | undefined;
  var g_VideoSource: MediaElementAudioSourceNode | null | undefined;
  var g_VideoGainNode: GainNode | null | undefined;
  var g_VideoHls: Hls | null | undefined;
  var g_VideoHlsConfig: HlsConfig | null | undefined;
  var g_VideoPlayerType: number | null | undefined;

  // TextureCanvas, Gyro
  var g_TextCanvasList: (HTMLCanvasElement | null)[] | undefined;
  var g_GyroActive: boolean | undefined;

  var g_hel_UploadFileImage: string | ArrayBuffer | null | undefined;
  var g_InputFile: HTMLElement | null | undefined;
  var g_VCUID: string | undefined;
  var g_configurl: HTMLElement | null | undefined;
  var g_localText_configurl: HTMLElement | null | undefined;
  var g_username: HTMLElement | null | undefined;
  var g_localText_username: HTMLInputElement | null | undefined;

  // Network(named for Skyway as Default) and Others
  /**
   * @remarks
   * C++からアクセスあり
   */
  var g_Skyway_Start: boolean | undefined;
  var g_Status: HTMLElement | null | undefined;
  var g_Log: HTMLElement | null | undefined;
  var g_Skyway_MyName: string | undefined;
  var g_localStream: MediaStream | null | undefined;
  var g_SKYWAY_KEY: string | undefined;
  var g_Skyway_MyAvatar: number | undefined;
  var g_Skyway_MyAvatar_ForSend: number | undefined;
  var g_Skyway_Use_CustomAvatarIcon: boolean | undefined;
  var g_Skyway_CustomAvatarIcon_URL: string | undefined;
  var g_skyway_set_mic_state_timer: number | null | undefined;
  var g_skyway_msg: HTMLElement | null | undefined;
  var g_skyway_localText: HTMLInputElement | null | undefined;

  // XRWorldIngame
  var g_xrwFriendCode: string | undefined;
  var g_hel_UseSkywayAudioGain: boolean | undefined;
  var g_DataFolderName: string | undefined;

  // procedural call oriented bridges
  var g_ActionProcedureList: (ProcedureCallFunc | undefined)[] | undefined;

  // ProcedureOnclickedAvatarCallFunc
  var g_OnclickedAvatarProcedureList: ProcedureOnclickedAvatarCallFunc[] | undefined;

  // ProcedureOnloadedCallFunc
  var g_OnloadedProcedureList: ProcedureOnloadedCallFunc[] | undefined;

  // ProcedureOnresizeCallFunc
  var g_OnresizeProcedureList: ProcedureOnresizeCallFunc[] | undefined;

  // ProcedureSkywayCommandCallFunc
  var g_SkywayCommandProcedureList: ProcedureSkywayCommandCallFunc[] | undefined;

  // ProcedureSkywayMyinfoCallFunc
  var g_SkywayMyinfoProcedureList: ProcedureSkywayMyinfoCallFunc[] | undefined;

  // メインキャンバスの設定をこちらで行う
  var g_ReflectCanvasSizeMode: boolean | undefined;
  /* eslint-enable */
}

Object.assign(
  globalThis,
  {
    // config
    g_WorldID: undefined,
    g_Skyway_RoomID: '',
    g_Spot: undefined,
    g_ShowWorldSelect: undefined,
    g_Logined: false,
    g_CanvasFileName: 'Canvas/CanvasList.json',
    g_PackFileList: [],
    g_CompImageListFileName: '',
    g_CompImageListUseCDN: false,
    g_CurrentUserName: 'Guest',
    g_MainCanvas: undefined,
    g_CanvasIsPortrait: false,

    // render
    g_UseWebGL10: false,
    g_extPVRTC: null,
    g_TextureCompression_PVRTC: false,
    g_extETC2: null,
    g_TextureCompression_ETC2: false,
    g_extASTC: null,
    g_TextureCompression_ASTC: false,
    g_extDXT: null,
    g_TextureCompression_DXT: false,
    g_Use_EXT_shader_texture_lod: false,
    g_Use_EXT_color_buffer_half_float: false,
    g_EXT_color_buffer_float: null,
    g_EXT_color_buffer_half_float: null,
    g_Use_EXT_color_buffer: false,
    g_OES_texture_half_float: null,
    g_EXT_shader_texture_lod: null,

    // audio
    g_hel_MixerTabActive: 1.0,
    g_hel_MixerFade: [...INITIAL_MIXER_FADER_SETTING] as const,
    g_hel_MixerDistance: [...INITIAL_MIXER_DISTANCE_SETTING] as const,
    g_hel_MixerMute: [...INITIAL_MIXER_MUTE_SETTING] as const,
    g_hel_MixerMasterVolume: 1.0,
    g_hel_MixerMasterMute: 0,
    g_hel_UseLegacyAudio: false,

    g_hel_Mixer: [...INITIAL_MIXER_SETTING] as const,

    // video
    g_Video: null,
    g_videocanplay: false,

    g_videopaused: false,
    g_videoended: false,
    g_VideoAudioContext: null,
    g_VideoSource: null,
    g_VideoGainNode: null,
    g_VideoHls: null,
    g_VideoHlsConfig: null,
    g_VideoPlayerType: null,

    // TextureCanvas, Gyro
    g_TextCanvasList: [],
    g_GyroActive: false,

    g_hel_UploadFileImage: null,
    g_InputFile: document.getElementById('input_file'),
    g_VCUID: '',
    g_configurl: document.getElementById('configurl'),
    g_localText_configurl: document.getElementById('configurl-text'),
    g_username: document.getElementById('username'),
    g_localText_username: document.getElementById('username-text') as HTMLInputElement | null,

    // Network(named for Skyway as Default) and Others
    g_Skyway_Start: false,
    g_Status: null,
    g_Log: null,

    g_Skyway_MyName: 'Guest',
    g_localStream: null,
    g_SKYWAY_KEY: '37825849-7771-49bd-890e-61971fb4f431',
    g_Skyway_MyAvatar: 0,
    g_Skyway_MyAvatar_ForSend: 0,
    g_Skyway_Use_CustomAvatarIcon: false,
    g_Skyway_CustomAvatarIcon_URL: '',
    g_skyway_set_mic_state_timer: null,
    g_skyway_msg: document.getElementById('msg'),
    g_skyway_localText: document.getElementById('js-local-text') as HTMLInputElement | null,

    // XRWorldIngame
    g_xrwFriendCode: '',
    g_hel_UseSkywayAudioGain: true,
    g_DataFolderName: 'data',

    // procedural call oriented bridges
    g_ActionProcedureList: [],

    // ProcedureOnclickedAvatarCallFunc
    g_OnclickedAvatarProcedureList: [],

    // ProcedureOnloadedCallFunc
    g_OnloadedProcedureList: [],

    // ProcedureOnresizeCallFunc
    g_OnresizeProcedureList: [],

    // ProcedureSkywayCommandCallFunc
    g_SkywayCommandProcedureList: [],

    // ProcedureSkywayMyinfoCallFunc
    g_SkywayMyinfoProcedureList: [],

    // メインキャンバスの設定をこちらで行う
    g_ReflectCanvasSizeMode: true
  } satisfies Partial<typeof globalThis>,
  {
    // render
    g_Use_OES_texture_half_float: false,
    isIntelWebGLPlatform,

    // Network(named for Skyway as Default) and Others
    g_SkywayPlayerList: [],
    g_MainCanvas4XR: null
  }
);

const mainCanvas = document.getElementById('canvas') as HTMLCanvasElement | null;

if (mainCanvas) {
  mainCanvas.addEventListener('webglcontextlost', (event) => {
    hel_show_fatal_error_dialog('HEL02000000', 'webglcontextlost');
  });
  setMainCanvasDOMElement(mainCanvas);
}
hel_reflectCanvasSize();

if (mainCanvas) {
  mainCanvas.onclick = function () {
    globalThis.g_MainCanvas!.focus();
  };
  mainCanvas.oncontextmenu = (e: Event) => {
    e.preventDefault();
  };
}

window.onresize = hel_resizeWindow;

defineSetstateForHeliModule();

// Video Recorder
const g_VideoRecorder = null;
const g_RecordedVideoBlobObj = null;
const g_RecordedVideoBlobURL = null;
const g_VideoRecorderExtension = null;
const g_VideoRecordCanvas = null;
const g_VideoRecordContext = null;
